import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import authService from "../services/discordAuth";
import usePageTitle from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";
import { trackEvent } from "../utils/analytics";
import "./AddBotPage.css";

function AddBotPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  usePageTitle(t("addBot.pageTitle"), t("addBot.pageDesc"));

  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem("add_bot_current_step");
    return saved ? parseInt(saved, 10) : 1;
  });
  const [tagsPage, setTagsPage] = useState(0);
  const [featuresPage, setFeaturesPage] = useState(0);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [categorySuggestion, setCategorySuggestion] = useState(null); // Sugestão de categoria similar
  const [showLongDescPreview, setShowLongDescPreview] = useState(false);
  const [dbCategories, setDbCategories] = useState([]); // Categorias vindas do banco
  const [dbTags, setDbTags] = useState([]); // Tags vindas do banco
  const [dbFeatures, setDbFeatures] = useState([]); // Funcionalidades vindas do banco
  const [dbLanguages, setDbLanguages] = useState([]); // Idiomas vindos do banco
  const [languageInput, setLanguageInput] = useState("");
  const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false);
  const [botData, setBotData] = useState(() => {
    const saved = localStorage.getItem("add_bot_data");
    return saved
      ? JSON.parse(saved)
      : {
          // Etapa 1: Verificação
          botId: "",

          // Etapa 2: Detalhes
          botName: "",
          shortDescription: "",
          longDescription: "",
          prefix: "",
          inviteUrl: "",
          supportServer: "",
          website: "",
          categories: [], // Múltiplas categorias
          tags: [],
          features: [],
          languages: [],

          // Etapa 3: Comandos
          commands: [], // Array de {name, description, usage, category}

          // Etapa 4: Revisão
          reviewNotes: "",
          testInstructions: "",
          additionalInfo: "",

          // Etapa 5: Redes Sociais
          github: "",
          twitter: "",
          instagram: "",
          youtube: "",
          discord: "",

          // Dados verificados
          isVerified: false,
          isOwner: false,
          botInfo: null,
        };
  });

  const [auditResult, setAuditResult] = useState(null);
  const [isAuditing, setIsAuditing] = useState(false);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const categoryInputRef = useRef(null);
  const languageInputRef = useRef(null);

  // Verifica se está logado
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      authService.login();
    }
  }, [navigate]);

  // Salva automaticamente no localStorage
  useEffect(() => {
    localStorage.setItem("add_bot_data", JSON.stringify(botData));
  }, [botData]);

  useEffect(() => {
    if (currentStep === 7) {
      localStorage.removeItem("add_bot_current_step");
    } else {
      localStorage.setItem("add_bot_current_step", currentStep.toString());
    }
  }, [currentStep]);

  // Aviso ao tentar fechar a página com dados preenchidos
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Se já passou da primeira etapa e não chegou na última, avisa
      if (currentStep > 1 && currentStep < 7) {
        e.preventDefault();
        e.returnValue = t("addBot.beforeUnload");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [currentStep]);

  // Busca dados do banco (Categorias, Tags, Features)
  useEffect(() => {
    const fetchInitialData = async () => {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      try {
        // Categorias
        const catRes = await fetch(`${BACKEND_URL}/api/bots/categories`);
        if (catRes.ok) {
          const data = await catRes.json();
          setDbCategories(data.map((c) => c.name));
        }

        // Tags
        const tagRes = await fetch(`${BACKEND_URL}/api/bots/tags`);
        if (tagRes.ok) {
          const data = await tagRes.json();
          setDbTags(data.map((t) => t.name));
        }

        // Features
        const featRes = await fetch(`${BACKEND_URL}/api/bots/features/available`);
        if (featRes.ok) {
          const data = await featRes.json();
          setDbFeatures(data.map((f) => f.title));
        }

        // Idiomas
        const langRes = await fetch(`${BACKEND_URL}/api/bots/languages`);
        if (langRes.ok) {
          const data = await langRes.json();
          setDbLanguages(data);
        }
      } catch (error) {
        console.error("Erro ao buscar dados iniciais:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryInputRef.current && !categoryInputRef.current.contains(event.target)) {
        setShowCategorySuggestions(false);
      }
      if (languageInputRef.current && !languageInputRef.current.contains(event.target)) {
        setShowLanguageSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Listas disponíveis vindas diretamente do banco de dados
  const availableCategoriesList = dbCategories;
  const availableTags = dbTags;
  const availableFeatures = dbFeatures;

  // Paginação de tags e features
  const ITEMS_PER_PAGE = 12;
  const tagsToShow = availableTags.slice(tagsPage * ITEMS_PER_PAGE, (tagsPage + 1) * ITEMS_PER_PAGE);
  const featuresToShow = availableFeatures.slice(featuresPage * ITEMS_PER_PAGE, (featuresPage + 1) * ITEMS_PER_PAGE);
  const totalTagsPages = Math.ceil(availableTags.length / ITEMS_PER_PAGE);
  const totalFeaturesPages = Math.ceil(availableFeatures.length / ITEMS_PER_PAGE);

  const filteredCategories = availableCategoriesList.filter((cat) => cat.toLowerCase().includes(categoryInput.toLowerCase()) && !botData.categories.includes(cat));

  const filteredLanguages = dbLanguages.filter(
    (lang) =>
      (lang.name.toLowerCase().includes(languageInput.toLowerCase()) || lang.native_name.toLowerCase().includes(languageInput.toLowerCase())) &&
      !botData.languages.includes(lang.name),
  );

  // Valida etapa atual
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!botData.botId) {
        newErrors.botId = t("addBot.errors.botIdRequired");
      } else if (!/^\d{17,19}$/.test(botData.botId)) {
        newErrors.botId = t("addBot.errors.botIdInvalid");
      }
      if (!botData.isVerified) {
        newErrors.botId = t("addBot.errors.botNotVerified");
      }
    }

    if (step === 2) {
      if (!botData.botName || botData.botName.length < 2) {
        newErrors.botName = t("addBot.errors.botNameRequired");
      }
      if (!botData.shortDescription || botData.shortDescription.length < 20) {
        newErrors.shortDescription = t("addBot.errors.shortDescRequired");
      }
      if (botData.shortDescription && botData.shortDescription.length > 200) {
        newErrors.shortDescription = t("addBot.errors.shortDescTooLong");
      }
      if (!botData.longDescription || botData.longDescription.length < 100) {
        newErrors.longDescription = t("addBot.errors.longDescRequired");
      }
      if (botData.longDescription && botData.longDescription.length > 2000) {
        newErrors.longDescription = t("addBot.errors.longDescTooLong");
      }
      if (!botData.prefix) {
        newErrors.prefix = t("addBot.errors.prefixRequired");
      }
      if (!botData.inviteUrl) {
        newErrors.inviteUrl = t("addBot.errors.inviteUrlRequired");
      }
      if (botData.categories.length === 0) {
        newErrors.categories = t("addBot.errors.categoryRequired");
      }
      if (botData.categories.length > 10) {
        newErrors.categories = t("addBot.errors.categoryMax");
      }
      if (botData.tags.length === 0) {
        newErrors.tags = t("addBot.errors.tagRequired");
      }
      if (botData.tags.length > 10) {
        newErrors.tags = t("addBot.errors.tagMax");
      }
      if (!botData.features.length) {
        newErrors.features = t("addBot.errors.featureRequired");
      }
      if (botData.features.length > 10) {
        newErrors.features = t("addBot.errors.featureMax");
      }
      if (botData.languages.length === 0) {
        newErrors.languages = t("addBot.errors.languageRequired");
      }
      if (botData.languages.length > 10) {
        newErrors.languages = t("addBot.errors.languageMax");
      }
    }

    if (step === 3) {
      if (botData.commands.length < 3) {
        newErrors.commands = t("addBot.errors.commandsMin");
      }
      if (botData.commands.length > 10) {
        newErrors.commands = t("addBot.errors.commandsMax");
      }
      // Validar cada comando
      botData.commands.forEach((cmd, index) => {
        if (!cmd.name || cmd.name.trim().length === 0) {
          newErrors[`command_${index}_name`] = t("addBot.errors.commandNameRequired");
        }
        if (!cmd.description || cmd.description.trim().length < 10) {
          newErrors[`command_${index}_description`] = t("addBot.errors.commandDescMin");
        }
        if (!cmd.usage || cmd.usage.trim().length === 0) {
          newErrors[`command_${index}_usage`] = t("addBot.errors.commandUsageRequired");
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Verifica o bot
  const handleVerifyBot = async () => {
    if (!botData.botId || !/^\d{17,19}$/.test(botData.botId)) {
      setErrors({ botId: t("addBot.errors.botIdInvalid") });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

      const response = await fetch(`${BACKEND_URL}/api/bots/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ botId: botData.botId }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors({ botId: data.error || t("addBot.errors.verifyError") });
        return;
      }

      if (!data.isBot) {
        setErrors({ botId: t("addBot.errors.notABot") });
        return;
      }

      // Bot verificado com sucesso
      setBotData({
        ...botData,
        isVerified: true,
        isOwner: true, // Por enquanto sempre true (não verificamos ownership)
        botInfo: data.bot,
        botName: data.bot.username,
      });
    } catch (error) {
      console.error("Erro ao verificar bot:", error);
      setErrors({ botId: t("addBot.errors.verifyGenericError") });
    } finally {
      setLoading(false);
    }
  };

  // Próxima etapa
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  // Etapa anterior
  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  // Submeter bot
  const handleSubmit = async () => {
    setLoading(true);
    setIsAuditing(true);
    setErrors({});

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
      const token = authService.getAccessToken();

      if (!token) {
        setErrors({
          submit: t("addBot.errors.loginRequired"),
        });
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/bots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...botData,
          features: botData.features, // Envia apenas o array de strings (títulos)
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors({ submit: data.error || t("addBot.errors.submitError") });
        return;
      }

      // Sucesso!
      trackEvent("add_bot_success", {
        bot_id: botData.botId,
        bot_name: botData.botName,
      });

      setAuditResult(data.audit); // Salva o resultado da I.A
      setIsAuditing(false); // Fecha o modal de auditoria
      localStorage.removeItem("add_bot_data");
      localStorage.removeItem("add_bot_current_step");
      setCurrentStep(7);
    } catch (error) {
      console.error("Erro ao enviar bot:", error);
      setErrors({
        submit: t("addBot.errors.submitGenericError"),
      });
    } finally {
      setLoading(false);
      setIsAuditing(false);
    }
  };

  // Adiciona categoria (predefinida ou customizada)
  const handleAddCategory = (category) => {
    const trimmedCategory = category.trim();
    if (trimmedCategory && !botData.categories.includes(trimmedCategory) && botData.categories.length < 10) {
      setBotData({
        ...botData,
        categories: [...botData.categories, trimmedCategory],
      });
      setCategoryInput("");
      setShowCategorySuggestions(false);
      setCategorySuggestion(null);
    }
  };

  // Remove categoria
  const removeCategory = (catToRemove) => {
    setBotData({
      ...botData,
      categories: botData.categories.filter((cat) => cat !== catToRemove),
    });
  };

  // Gerenciamento de Idiomas
  const handleLanguageInput = (e) => {
    const value = e.target.value;
    setLanguageInput(value);
    setShowLanguageSuggestions(value.length > 0);
  };

  const removeLanguage = (langToRemove) => {
    setBotData({
      ...botData,
      languages: botData.languages.filter((l) => l !== langToRemove),
    });
  };

  const selectLanguageSuggestion = (langName) => {
    if (!botData.languages.includes(langName)) {
      setBotData({
        ...botData,
        languages: [...botData.languages, langName],
      });
    }
    setLanguageInput("");
    setShowLanguageSuggestions(false);
  };

  // Gerencia input de categoria com validação em tempo real
  const handleCategoryInput = async (e) => {
    const value = e.target.value;

    // Se digitou vírgula, adiciona a categoria
    if (value.endsWith(",")) {
      const categoryName = value.slice(0, -1).trim();
      if (categoryName) {
        handleAddCategory(categoryName);
      }
      return;
    }

    setCategoryInput(value);
    setShowCategorySuggestions(true);

    // Validar em tempo real se digitou mais de 2 caracteres
    if (value.trim().length > 2) {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
        const response = await fetch(`${BACKEND_URL}/api/bots/categories/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: value.trim() }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.similarMatch) {
            setCategorySuggestion({
              typed: value.trim(),
              suggested: data.similarMatch,
              message: data.suggestion,
            });
          } else {
            setCategorySuggestion(null);
          }
        }
      } catch (error) {
        console.error("Erro ao validar categoria:", error);
      }
    } else {
      setCategorySuggestion(null);
    }
  };

  // Gerencia teclas especiais
  const handleCategoryKeyDown = (e) => {
    if (e.key === "Enter" && categoryInput.trim()) {
      e.preventDefault();
      handleAddCategory(categoryInput);
    } else if (e.key === "Backspace" && !categoryInput && botData.categories.length > 0) {
      // Remove última categoria se input está vazio
      removeCategory(botData.categories[botData.categories.length - 1]);
    }
  };

  // Toggle tag
  const toggleTag = (tag) => {
    if (botData.tags.includes(tag)) {
      setBotData({ ...botData, tags: botData.tags.filter((t) => t !== tag) });
    } else {
      setBotData({ ...botData, tags: [...botData.tags, tag] });
    }
  };

  // Toggle feature
  const toggleFeature = (feature) => {
    if (botData.features.includes(feature)) {
      setBotData({
        ...botData,
        features: botData.features.filter((f) => f !== feature),
      });
    } else {
      setBotData({ ...botData, features: [...botData.features, feature] });
    }
  };

  // Gerenciamento de Comandos
  const addCommand = () => {
    if (botData.commands.length < 10) {
      setBotData({
        ...botData,
        commands: [...botData.commands, { name: "", description: "", usage: "", category: "Geral" }],
      });
    }
  };

  const removeCommand = (index) => {
    setBotData({
      ...botData,
      commands: botData.commands.filter((_, i) => i !== index),
    });
  };

  const updateCommand = (index, field, value) => {
    const updatedCommands = [...botData.commands];
    updatedCommands[index][field] = value;
    setBotData({ ...botData, commands: updatedCommands });
  };

  return (
    <div className="add-bot-page">
      <div className="container">
        <div className="add-bot-header">
          <h1>{t("addBot.header.title")}</h1>
          <p>{t("addBot.header.subtitle")}</p>
        </div>

        <div className="add-bot-content">
          {/* Sidebar com etapas */}
          <div className="steps-sidebar">
            <div className="steps-list">
              {[
                { num: 1, key: "1", icon: "🔍" },
                { num: 2, key: "2", icon: "📝" },
                { num: 3, key: "3", icon: "⚡" },
                { num: 4, key: "4", icon: "📋" },
                { num: 5, key: "5", icon: "🌐" },
                { num: 6, key: "6", icon: "✅" },
                { num: 7, key: "7", icon: "🎉" },
              ].map((step) => (
                <div key={step.num} className={`step-item ${currentStep === step.num ? "active" : ""} ${currentStep > step.num ? "completed" : ""}`}>
                  <div className="step-number">{currentStep > step.num ? "✓" : step.icon}</div>
                  <div className="step-info">
                    <div className="step-title">{t(`addBot.steps.${step.key}`)}</div>
                    <div className="step-subtitle">{t("addBot.steps.label", { num: step.num })}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Formulário */}
          <div className={`form-container`}>
            {/* Etapa 1: Verificação */}
            {currentStep === 1 && (
              <div className="form-step">
                <h2>🔍 {t("addBot.step1.title")}</h2>
                <p className="step-description">{t("addBot.step1.subtitle")}</p>

                <div className="form-group">
                  <label>{t("addBot.step1.labelId")}</label>
                  <input
                    type="text"
                    placeholder={t("addBot.step1.placeholderId")}
                    value={botData.botId}
                    onChange={(e) => setBotData({ ...botData, botId: e.target.value })}
                    className={errors.botId ? "error" : ""}
                    disabled={botData.isVerified}
                  />
                  {errors.botId && <span className="error-message">{errors.botId}</span>}
                  <span className="help-text">{t("addBot.step1.helpId")}</span>
                </div>

                {!botData.isVerified ? (
                  <button className="btn btn-primary" onClick={handleVerifyBot} disabled={loading}>
                    {loading ? t("addBot.step1.btnVerifying") : t("addBot.step1.btnVerify")}
                  </button>
                ) : (
                  <div className="verification-success">
                    <div className="success-icon">✅</div>
                    <div className="success-info">
                      <h3>{t("addBot.step1.successTitle")}</h3>
                      <p>
                        <strong>{botData.botInfo.username}</strong>
                      </p>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      localStorage.removeItem("add_bot_data");
                      localStorage.removeItem("add_bot_current_step");
                      navigate("/");
                    }}
                  >
                    {t("addBot.actions.cancel")}
                  </button>
                  <button className="btn btn-primary" onClick={handleNext} disabled={!botData.isVerified}>
                    {t("addBot.actions.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 2: Detalhes do Bot */}
            {currentStep === 2 && (
              <div className="form-step">
                <h2>📝 {t("addBot.step2.title")}</h2>
                <p className="step-description">{t("addBot.step2.subtitle")}</p>

                <div className="form-group">
                  <label>{t("addBot.step2.labelName")}</label>
                  <input
                    type="text"
                    placeholder={t("addBot.step2.placeholderName")}
                    value={botData.botName}
                    onChange={(e) => setBotData({ ...botData, botName: e.target.value })}
                    className={errors.botName ? "error" : ""}
                  />
                  {errors.botName && <span className="error-message">{errors.botName}</span>}
                </div>

                <div className="form-group">
                  <label>
                    {t("addBot.step2.labelShortDesc")} ({botData.shortDescription.length}/200)
                  </label>
                  <input
                    type="text"
                    placeholder={t("addBot.step2.placeholderShortDesc")}
                    value={botData.shortDescription}
                    onChange={(e) =>
                      setBotData({
                        ...botData,
                        shortDescription: e.target.value,
                      })
                    }
                    maxLength={200}
                    className={errors.shortDescription ? "error" : ""}
                  />
                  {errors.shortDescription && <span className="error-message">{errors.shortDescription}</span>}
                </div>

                <div className="form-group">
                  <div className="label-with-action">
                    <label>
                      {t("addBot.step2.labelLongDesc")} ({botData.longDescription.length}/2000)
                    </label>
                    <button type="button" className="btn-preview-toggle" onClick={() => setShowLongDescPreview(!showLongDescPreview)}>
                      {showLongDescPreview ? "✍️ Editar" : "👁️ Preview"}
                    </button>
                  </div>

                  {showLongDescPreview ? (
                    <div className="markdown-preview-container markdown-body">
                      <ReactMarkdown>{botData.longDescription || "_Sem descrição para visualizar_"}</ReactMarkdown>
                    </div>
                  ) : (
                    <textarea
                      placeholder={t("addBot.step2.placeholderLongDesc")}
                      value={botData.longDescription}
                      onChange={(e) =>
                        setBotData({
                          ...botData,
                          longDescription: e.target.value,
                        })
                      }
                      rows={8}
                      maxLength={2000}
                      className={errors.longDescription ? "error" : ""}
                    />
                  )}

                  {errors.longDescription && (
                    <span className="error-message" style={{ margin: 0 }}>
                      {errors.longDescription}
                    </span>
                  )}
                  <small className="help-text">Suporta Markdown (Negrito, Itálico, Listas, etc)</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t("addBot.step2.labelPrefix")}</label>
                    <input
                      type="text"
                      placeholder={t("addBot.step2.placeholderPrefix")}
                      value={botData.prefix}
                      onChange={(e) => setBotData({ ...botData, prefix: e.target.value })}
                      className={errors.prefix ? "error" : ""}
                    />
                    {errors.prefix && <span className="error-message">{errors.prefix}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      {t("addBot.step2.labelCategories")} ({botData.categories.length}/10)
                    </label>

                    {/* Tags selecionadas */}
                    {botData.categories.length > 0 && (
                      <div className="selected-categories">
                        {botData.categories.map((cat) => (
                          <div key={cat} className="category-tag">
                            {cat}
                            <button type="button" className="category-remove" onClick={() => removeCategory(cat)}>
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Input com autocomplete */}
                    <div className="category-input-container" ref={categoryInputRef}>
                      <input
                        type="text"
                        placeholder={t("addBot.step2.placeholderCategories")}
                        value={categoryInput}
                        onChange={handleCategoryInput}
                        onKeyDown={handleCategoryKeyDown}
                        onFocus={() => setShowCategorySuggestions(true)}
                        disabled={botData.categories.length >= 10}
                        className={errors.categories ? "error" : ""}
                      />

                      {/* Sugestões */}
                      {showCategorySuggestions && filteredCategories.length > 0 && (
                        <div className="category-suggestions">
                          {filteredCategories.map((cat) => (
                            <div key={cat} className="suggestion-item" onClick={() => handleAddCategory(cat)}>
                              {cat}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Aviso de categoria similar */}
                      {categorySuggestion && (
                        <div className="category-similar-warning">
                          <span className="warning-icon">⚠️</span>
                          <div className="warning-content">
                            <p>{categorySuggestion.message}</p>
                            <div className="warning-actions">
                              <button
                                type="button"
                                className="btn-use-suggested"
                                onClick={() => {
                                  handleAddCategory(categorySuggestion.suggested);
                                  setCategorySuggestion(null);
                                }}
                              >
                                {t("addBot.step2.btnUseSuggested", {
                                  suggested: categorySuggestion.suggested,
                                })}
                              </button>
                              <button
                                type="button"
                                className="btn-keep-typed"
                                onClick={() => {
                                  handleAddCategory(categorySuggestion.typed);
                                  setCategorySuggestion(null);
                                }}
                              >
                                {t("addBot.step2.btnKeepTyped", {
                                  typed: categorySuggestion.typed,
                                })}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <span className="help-text">{t("addBot.step2.helpCategories")}</span>
                    {errors.categories && <span className="error-message">{errors.categories}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    {t("addBot.step2.labelLanguages")} ({botData.languages.length}/10)
                  </label>

                  {/* Idiomas selecionados */}
                  {botData.languages.length > 0 && (
                    <div className="selected-categories">
                      {botData.languages.map((lang) => (
                        <div key={lang} className="category-tag">
                          {lang}
                          <button type="button" className="category-remove" onClick={() => removeLanguage(lang)}>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input com autocomplete */}
                  <div className="category-input-container" ref={languageInputRef}>
                    <input
                      type="text"
                      placeholder={t("addBot.step2.placeholderLanguages")}
                      value={languageInput}
                      onChange={handleLanguageInput}
                      onFocus={() => languageInput.length > 0 && setShowLanguageSuggestions(true)}
                      className={errors.languages ? "error" : ""}
                    />

                    {showLanguageSuggestions && filteredLanguages.length > 0 && (
                      <div className="category-suggestions">
                        {filteredLanguages.map((lang) => (
                          <div key={lang.code} className="suggestion-item suggestion-item-lang" onClick={() => selectLanguageSuggestion(lang.name)}>
                            <span className="suggestion-flag">{lang.flag}</span>
                            <div className="suggestion-info">
                              <span className="suggestion-name">{lang.name}</span>
                              <span className="suggestion-native">{lang.native_name}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.languages && <span className="error-message">{errors.languages}</span>}
                </div>

                <div className="form-group">
                  <label>{t("addBot.step2.labelInvite")}</label>
                  <input
                    type="url"
                    placeholder={t("addBot.step2.placeholderInvite")}
                    value={botData.inviteUrl}
                    onChange={(e) => setBotData({ ...botData, inviteUrl: e.target.value })}
                    className={errors.inviteUrl ? "error" : ""}
                  />
                  {errors.inviteUrl && <span className="error-message">{errors.inviteUrl}</span>}
                </div>

                <div className="form-group">
                  <label>{t("addBot.step2.labelSupport")}</label>
                  <input
                    type="url"
                    placeholder={t("addBot.step2.placeholderSupport")}
                    value={botData.supportServer}
                    onChange={(e) => setBotData({ ...botData, supportServer: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>{t("addBot.step2.labelWebsite")}</label>
                  <input
                    type="url"
                    placeholder={t("addBot.step2.placeholderWebsite")}
                    value={botData.website}
                    onChange={(e) => setBotData({ ...botData, website: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>
                    {t("addBot.step2.labelTags", {
                      count: botData.tags.length,
                    })}
                  </label>
                  <div className="carousel-container">
                    <div className="tags-grid">
                      {tagsToShow.map((tag) => (
                        <div key={tag} className={`tag-item ${botData.tags.includes(tag) ? "selected" : ""}`} onClick={() => toggleTag(tag)}>
                          {tag}
                        </div>
                      ))}
                    </div>
                    <div className="carousel-controls">
                      <button type="button" className="btn btn-sm btn-secondary btn-voltar" onClick={() => setTagsPage(Math.max(0, tagsPage - 1))} disabled={tagsPage === 0}>
                        {t("addBot.carousel.prev")}
                      </button>
                      <span className="carousel-indicator">
                        {t("addBot.carousel.indicator", {
                          current: tagsPage + 1,
                          total: totalTagsPages,
                        })}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary btn-proximo"
                        onClick={() => setTagsPage(Math.min(totalTagsPages - 1, tagsPage + 1))}
                        disabled={tagsPage === totalTagsPages - 1}
                      >
                        {t("addBot.carousel.next")}
                      </button>
                    </div>
                  </div>
                  {errors.tags && <span className="error-message">{errors.tags}</span>}
                </div>

                <div className="form-group">
                  <label>
                    {t("addBot.step2.labelFeatures", {
                      count: botData.features.length,
                    })}
                  </label>
                  <div className="carousel-container">
                    <div className="tags-grid">
                      {featuresToShow.map((feature) => (
                        <div key={feature} className={`tag-item ${botData.features.includes(feature) ? "selected" : ""}`} onClick={() => toggleFeature(feature)}>
                          {feature}
                        </div>
                      ))}
                    </div>
                    <div className="carousel-controls">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary btn-voltar"
                        onClick={() => setFeaturesPage(Math.max(0, featuresPage - 1))}
                        disabled={featuresPage === 0}
                      >
                        {t("addBot.carousel.prev")}
                      </button>
                      <span className="carousel-indicator">
                        {t("addBot.carousel.indicator", {
                          current: featuresPage + 1,
                          total: totalFeaturesPages,
                        })}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary btn-proximo"
                        onClick={() => setFeaturesPage(Math.min(totalFeaturesPages - 1, featuresPage + 1))}
                        disabled={featuresPage === totalFeaturesPages - 1}
                      >
                        {t("addBot.carousel.next")}
                      </button>
                    </div>
                  </div>
                  {errors.features && <span className="error-message">{errors.features}</span>}
                </div>

                <div className="form-actions">
                  <button className="btn btn-secondary btn-voltar" onClick={handlePrevious}>
                    {t("addBot.actions.prev")}
                  </button>
                  <button className="btn btn-primary btn-proximo" onClick={handleNext}>
                    {t("addBot.actions.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 3: Comandos */}
            {currentStep === 3 && (
              <div className="form-step">
                <h2>⚡ {t("addBot.step3.title")}</h2>
                <p className="step-description">{t("addBot.step3.subtitle")}</p>

                <div className="commands-container">
                  {botData.commands.map((command, index) => (
                    <div key={index} className="command-card">
                      <div className="command-header">
                        <h3>{t("addBot.step3.commandLabel", { num: index + 1 })}</h3>
                        <button type="button" className="btn-remove-command" onClick={() => removeCommand(index)} title={t("addBot.step3.btnRemove")}>
                          ×
                        </button>
                      </div>

                      <div className="command-fields">
                        <div className="form-row">
                          <div className="form-group">
                            <label>{t("addBot.step3.labelCmdName")}</label>
                            <input
                              type="text"
                              placeholder={t("addBot.step3.placeholderCmdName")}
                              value={command.name}
                              onChange={(e) => updateCommand(index, "name", e.target.value)}
                              className={errors[`command_${index}_name`] ? "error" : ""}
                            />
                            {errors[`command_${index}_name`] && <span className="error-message">{errors[`command_${index}_name`]}</span>}
                          </div>

                          <div className="form-group">
                            <label>{t("addBot.step3.labelCmdCategory")}</label>
                            <select value={command.category} onChange={(e) => updateCommand(index, "category", e.target.value)}>
                              <option value="Geral">{t("addBot.step3.categories.Geral")}</option>
                              <option value="Moderação">{t("addBot.step3.categories.Moderação")}</option>
                              <option value="Música">{t("addBot.step3.categories.Música")}</option>
                              <option value="Diversão">{t("addBot.step3.categories.Diversão")}</option>
                              <option value="Utilidade">{t("addBot.step3.categories.Utilidade")}</option>
                              <option value="Economia">{t("addBot.step3.categories.Economia")}</option>
                              <option value="Jogos">{t("addBot.step3.categories.Jogos")}</option>
                              <option value="Configuração">{t("addBot.step3.categories.Configuração")}</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>{t("addBot.step3.labelCmdUsage")}</label>
                          <input
                            type="text"
                            placeholder={t("addBot.step3.placeholderCmdUsage")}
                            value={command.usage}
                            onChange={(e) => updateCommand(index, "usage", e.target.value)}
                            className={errors[`command_${index}_usage`] ? "error" : ""}
                          />
                          {errors[`command_${index}_usage`] && <span className="error-message">{errors[`command_${index}_usage`]}</span>}
                        </div>

                        <div className="form-group">
                          <label>
                            {t("addBot.step3.labelCmdDesc")} ({command.description.length}/200)
                          </label>
                          <textarea
                            placeholder={t("addBot.step3.placeholderCmdDesc")}
                            value={command.description}
                            onChange={(e) => updateCommand(index, "description", e.target.value)}
                            rows={3}
                            maxLength={200}
                            className={errors[`command_${index}_description`] ? "error" : ""}
                          />
                          {errors[`command_${index}_description`] && <span className="error-message">{errors[`command_${index}_description`]}</span>}
                        </div>
                      </div>
                    </div>
                  ))}

                  {botData.commands.length < 10 && (
                    <button type="button" className="btn-add-command" onClick={addCommand}>
                      {t("addBot.step3.btnAddCommand")}
                    </button>
                  )}

                  {errors.commands && <span className="error-message">{errors.commands}</span>}
                </div>

                <div className="form-actions">
                  <button className="btn btn-secondary btn-voltar" onClick={handlePrevious}>
                    {t("addBot.actions.prev")}
                  </button>
                  <button className="btn btn-primary btn-proximo" onClick={handleNext}>
                    {t("addBot.actions.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 4: Revisão */}
            {currentStep === 4 && (
              <div className="form-step">
                <h2>📋 {t("addBot.step4.title")}</h2>
                <p className="step-description">{t("addBot.step4.subtitle")}</p>

                <div className="form-group">
                  <label>{t("addBot.step4.labelNotes")}</label>
                  <textarea
                    placeholder={t("addBot.step4.placeholderNotes")}
                    value={botData.reviewNotes}
                    onChange={(e) => setBotData({ ...botData, reviewNotes: e.target.value })}
                    rows={5}
                  />
                  <span className="help-text">
                    {t("addBot.step4.helpNotes", {
                      count: botData.reviewNotes.length,
                    })}
                  </span>
                </div>

                <div className="form-group">
                  <label>{t("addBot.step4.labelTests")}</label>
                  <textarea
                    placeholder={t("addBot.step4.placeholderTests")}
                    value={botData.testInstructions}
                    onChange={(e) =>
                      setBotData({
                        ...botData,
                        testInstructions: e.target.value,
                      })
                    }
                    rows={6}
                  />
                  <span className="help-text">
                    {t("addBot.step4.helpTests", {
                      count: botData.testInstructions.length,
                    })}
                  </span>
                </div>

                <div className="form-group">
                  <label>{t("addBot.step4.labelAddInfo")}</label>
                  <textarea
                    placeholder={t("addBot.step4.placeholderAddInfo")}
                    value={botData.additionalInfo}
                    onChange={(e) => setBotData({ ...botData, additionalInfo: e.target.value })}
                    rows={5}
                  />
                  <span className="help-text">
                    {t("addBot.step4.helpAddInfo", {
                      count: botData.additionalInfo.length,
                    })}
                  </span>
                </div>

                <div className="info-box">
                  <div className="info-icon">💡</div>
                  <div className="info-content">
                    <strong>{t("addBot.step4.tipTitle")}</strong> {t("addBot.step4.tipDesc")}
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-secondary btn-voltar" onClick={handlePrevious}>
                    {t("addBot.actions.prev")}
                  </button>
                  <button className="btn btn-primary btn-proximo" onClick={handleNext}>
                    {t("addBot.actions.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 5: Redes Sociais */}
            {currentStep === 5 && (
              <div className="form-step">
                <h2>🌐 Redes Sociais</h2>
                <p className="step-description">
                  Adicione links para as redes sociais do seu bot. Todos os campos são opcionais, mas ajudam os usuários a encontrar mais informações.
                </p>

                <div className="social-grid">
                  <div className="form-group">
                    <label>
                      <span className="social-icon">🐙</span> {t("addBot.step5.labelGithub")}
                    </label>
                    <input
                      type="url"
                      placeholder={t("addBot.step5.placeholderGithub")}
                      value={botData.github}
                      onChange={(e) => setBotData({ ...botData, github: e.target.value })}
                    />
                    <span className="help-text">{t("addBot.step5.helpGithub")}</span>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="social-icon">🐦</span> {t("addBot.step5.labelTwitter")}
                    </label>
                    <input
                      type="url"
                      placeholder={t("addBot.step5.placeholderTwitter")}
                      value={botData.twitter}
                      onChange={(e) => setBotData({ ...botData, twitter: e.target.value })}
                    />
                    <span className="help-text">{t("addBot.step5.helpTwitter")}</span>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="social-icon">📸</span> {t("addBot.step5.labelInstagram")}
                    </label>
                    <input
                      type="url"
                      placeholder={t("addBot.step5.placeholderInstagram")}
                      value={botData.instagram}
                      onChange={(e) => setBotData({ ...botData, instagram: e.target.value })}
                    />
                    <span className="help-text">{t("addBot.step5.helpInstagram")}</span>
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="social-icon">📺</span> {t("addBot.step5.labelYoutube")}
                    </label>
                    <input
                      type="url"
                      placeholder={t("addBot.step5.placeholderYoutube")}
                      value={botData.youtube}
                      onChange={(e) => setBotData({ ...botData, youtube: e.target.value })}
                    />
                    <span className="help-text">{t("addBot.step5.helpYoutube")}</span>
                  </div>
                </div>

                <div className="info-box">
                  <div className="info-icon">🔗</div>
                  <div className="info-content">
                    <strong>{t("addBot.step5.whySocialTitle")}</strong> {t("addBot.step5.whySocialDesc")}
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-secondary btn-voltar" onClick={handlePrevious}>
                    {t("addBot.actions.prev")}
                  </button>
                  <button className="btn btn-primary btn-proximo" onClick={handleNext}>
                    {t("addBot.actions.next")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 6: Revisão Final */}
            {currentStep === 6 && (
              <div className="form-step">
                <h2>✅ {t("addBot.step6.title")}</h2>
                <p className="step-description">{t("addBot.step6.subtitle")}</p>

                {/* Informações do Bot */}
                {/* Informações do Bot */}
                <div className="review-section">
                  <h3 className="review-title">🤖 {t("addBot.step6.sectionInfo")}</h3>
                  <div className="review-grid">
                    <div className="review-item">
                      <span className="review-label">{t("addBot.step6.labelId")}</span>
                      <span className="review-value">{botData.botId}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">{t("addBot.step6.labelName")}</span>
                      <span className="review-value">{botData.botName}</span>
                    </div>
                    <div className="review-item">
                      <span className="review-label">{t("addBot.step6.labelPrefix")}</span>
                      <span className="review-value">{botData.prefix}</span>
                    </div>
                  </div>
                </div>

                {/* Descrições */}
                <div className="review-section">
                  <h3 className="review-title">📝 {t("addBot.step6.sectionDesc")}</h3>
                  <div className="review-item-full">
                    <span className="review-label">{t("addBot.step6.labelShortDesc")}</span>
                    <p className="review-text">{botData.shortDescription}</p>
                  </div>
                  <div className="review-item-full">
                    <span className="review-label">{t("addBot.step6.labelLongDesc")}</span>
                    <p className="review-text">{botData.longDescription}</p>
                  </div>
                </div>

                {/* Categorias e Tags */}
                <div className="review-section">
                  <h3 className="review-title">🏷️ {t("addBot.step6.sectionCat")}</h3>
                  <div className="review-item-full">
                    <span className="review-label">
                      {t("addBot.step6.labelCat", {
                        count: botData.categories.length,
                      })}
                    </span>
                    <div className="review-tags">
                      {botData.categories.map((cat) => (
                        <span key={cat} className="review-tag">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="review-item-full">
                    <span className="review-label">
                      {t("addBot.step6.labelTag", {
                        count: botData.tags.length,
                      })}
                    </span>
                    <div className="review-tags">
                      {botData.tags.map((tag) => (
                        <span key={tag} className="review-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="review-item-full">
                    <span className="review-label">
                      {t("addBot.step6.labelLang", {
                        count: botData.languages.length,
                      })}
                    </span>
                    <div className="review-tags">
                      {botData.languages.map((lang) => (
                        <span key={lang} className="review-tag">
                          {lang}
                        </span>
                      ))}
                    </div>
                  </div>
                  {botData.features.length > 0 && (
                    <div className="review-item-full">
                      <span className="review-label">
                        {t("addBot.step6.labelFeat", {
                          count: botData.features.length,
                        })}
                      </span>
                      <div className="review-tags">
                        {botData.features.map((feature) => (
                          <span key={feature} className="review-tag">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Comandos */}
                {botData.commands.length > 0 && (
                  <div className="review-section">
                    <h3 className="review-title">
                      ⚡{" "}
                      {t("addBot.step6.sectionCmd", {
                        count: botData.commands.length,
                      })}
                    </h3>
                    <div className="commands-review-list">
                      {botData.commands.map((cmd, index) => (
                        <div key={index} className="command-review-item">
                          <div className="command-review-header">
                            <strong>{cmd.name}</strong>
                            <span className="command-category-badge">{cmd.category}</span>
                          </div>
                          <div className="command-review-usage">{cmd.usage}</div>
                          <div className="command-review-description">{cmd.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="review-section">
                  <h3 className="review-title">🔗 {t("addBot.step6.sectionLinks")}</h3>
                  <div className="review-grid">
                    <div className="review-item">
                      <span className="review-label">{t("addBot.step6.labelInvite")}</span>
                      <a href={botData.inviteUrl} target="_blank" rel="noopener noreferrer" className="review-link">
                        {t("addBot.step6.btnViewLink")}
                      </a>
                    </div>
                    {botData.supportServer && (
                      <div className="review-item">
                        <span className="review-label">{t("addBot.step6.labelSupport")}</span>
                        <a href={botData.supportServer} target="_blank" rel="noopener noreferrer" className="review-link">
                          {t("addBot.step6.btnViewServer")}
                        </a>
                      </div>
                    )}
                    {botData.website && (
                      <div className="review-item">
                        <span className="review-label">{t("addBot.step6.labelWeb")}</span>
                        <a href={botData.website} target="_blank" rel="noopener noreferrer" className="review-link">
                          {t("addBot.step6.btnViewWeb")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Redes Sociais */}
                {(botData.github || botData.twitter || botData.instagram || botData.youtube) && (
                  <div className="review-section">
                    <h3 className="review-title">🌐 {t("addBot.step6.sectionSocial")}</h3>
                    <div className="review-grid">
                      {botData.github && (
                        <div className="review-item">
                          <span className="review-label">{t("addBot.step6.labelSocialGithub")}</span>
                          <a href={botData.github} target="_blank" rel="noopener noreferrer" className="review-link">
                            {t("addBot.step6.btnViewRepo")}
                          </a>
                        </div>
                      )}
                      {botData.twitter && (
                        <div className="review-item">
                          <span className="review-label">🐦 {t("addBot.step6.labelSocialTwitter")}</span>
                          <a href={botData.twitter} target="_blank" rel="noopener noreferrer" className="review-link">
                            {t("addBot.step6.btnViewProfile")}
                          </a>
                        </div>
                      )}
                      {botData.instagram && (
                        <div className="review-item">
                          <span className="review-label">📸 {t("addBot.step6.labelSocialInstagram")}</span>
                          <a href={botData.instagram} target="_blank" rel="noopener noreferrer" className="review-link">
                            {t("addBot.step6.btnViewProfile")}
                          </a>
                        </div>
                      )}
                      {botData.youtube && (
                        <div className="review-item">
                          <span className="review-label">📺 {t("addBot.step6.labelSocialYoutube")}</span>
                          <a href={botData.youtube} target="_blank" rel="noopener noreferrer" className="review-link">
                            {t("addBot.step6.btnViewChannel")}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Detalhes para Revisor */}
                {(botData.reviewNotes || botData.testInstructions || botData.additionalInfo) && (
                  <div className="review-section">
                    <h3 className="review-title">📋 {t("addBot.step6.sectionNotes")}</h3>
                    {botData.reviewNotes && (
                      <div className="review-item-full">
                        <span className="review-label">{t("addBot.step6.labelNotes")}</span>
                        <p className="review-text">{botData.reviewNotes}</p>
                      </div>
                    )}
                    {botData.testInstructions && (
                      <div className="review-item-full">
                        <span className="review-label">{t("addBot.step6.labelTests")}</span>
                        <p className="review-text">{botData.testInstructions}</p>
                      </div>
                    )}
                    {botData.additionalInfo && (
                      <div className="review-item-full">
                        <span className="review-label">{t("addBot.step6.labelExtra")}</span>
                        <p className="review-text">{botData.additionalInfo}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="info-box">
                  <div className="info-icon">⚠️</div>
                  <div className="info-content">
                    <strong>{t("addBot.step6.warningTitle")}</strong>
                    {t("addBot.step6.warningDesc")}
                  </div>
                </div>

                <div className="form-actions">
                  <button className="btn btn-secondary btn-voltar" onClick={handlePrevious}>
                    {t("addBot.actions.prev")}
                  </button>
                  <button className="btn btn-primary btn-proximo" onClick={handleSubmit} disabled={loading}>
                    {loading ? t("addBot.step6.btnSubmitting") : t("addBot.step6.btnSubmit")}
                  </button>
                </div>
              </div>
            )}

            {/* Etapa 7: Concluído */}
            {currentStep === 7 && (
              <div className="form-step success-step">
                <div className="success-content">
                  <div className="success-icon-large">{auditResult?.autoAction === "auto_rejected" ? "📋" : auditResult?.autoAction === "manual_review" ? "⚖️" : "🎉"}</div>
                  <h2>
                    {auditResult?.autoAction === "auto_rejected"
                      ? t("addBot.step7.rejectedTitle")
                      : auditResult?.autoAction === "manual_review"
                        ? t("addBot.step7.manualTitle")
                        : t("addBot.step7.successTitle")}
                  </h2>
                  <div className="step-description">
                    {auditResult?.autoAction === "auto_rejected" ? (
                      <p>{t("addBot.step7.rejectedDesc")}</p>
                    ) : auditResult?.autoAction === "manual_review" ? (
                      <p>{t("addBot.step7.manualDesc")}</p>
                    ) : (
                      <Trans i18nKey="addBot.step7.successDesc" values={{ name: botData.botName }}>
                        Parabéns! Seu bot <strong>{botData.botName}</strong> foi enviado com sucesso.
                      </Trans>
                    )}
                  </div>

                  {auditResult && (
                    <div className={`audit-badge ${auditResult.autoAction}`}>
                      {auditResult.autoAction === "auto_approved" ? (
                        <div className="audit-alert success">
                          <span className="icon">⚡</span>
                          <div>
                            <strong>{t("addBot.step7.statusApproved")}</strong>
                            <p>Excelente! Nossa equipe de Staff irá adicionar seu bot ao nosso servidor oficial em breve e os cargos serão atribuídos automaticamente.</p>
                          </div>
                        </div>
                      ) : auditResult.autoAction === "auto_rejected" ? (
                        <div className="audit-alert danger">
                          <span className="icon">❌</span>
                          <div>
                            <strong>{t("addBot.step7.statusRejected")}</strong>
                            <p>{t("addBot.step7.rejectedInstruction")}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="audit-alert warning">
                          <span className="icon">⚖️</span>
                          <div>
                            <strong>{t("addBot.step7.statusManual")}</strong>
                            <p>{t("addBot.step7.manualInstruction")}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="success-actions">
                    <button className="btn btn-primary" onClick={() => navigate(`/bots/${botData.botId}`)}>
                      {t("addBot.step7.btnViewBot")}
                    </button>
                    <button
                      className="btn btn-secondary btn-add-bot"
                      onClick={() => {
                        localStorage.removeItem("add_bot_data");
                        localStorage.removeItem("add_bot_current_step");
                        window.location.reload();
                      }}
                    >
                      {t("addBot.step7.btnAddAnother")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Auditoria em Andamento */}
        {isAuditing && (
          <div className="audit-modal-overlay">
            <div className="audit-modal">
              <div className="audit-loading-spinner">
                <div className="spinner-inner"></div>
                <div className="scanner-line"></div>
              </div>
              <h3>{t("addBot.step7.auditLoadingTitle")}</h3>
              <p>{t("addBot.step7.auditLoadingDesc")}</p>
              <div className="audit-steps-preview">
                <div className="audit-step-line active">
                  <span className="dot"></span>
                  <span>{t("addBot.step7.auditTitle")}...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddBotPage;
